import { Component, ElementRef, OnInit, computed, inject, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MasterData } from '@card-software/nexus-utilities';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbService } from '@card-software/library';
import { finalize } from 'rxjs';
import { Business, BusinessEntityType } from '../../../models/business';
import { BankAccountType, ProviderBankAccount, ProviderBankType, ProviderDetails } from '../../../models/provider/provider-details';
import { ProviderBankForm, ProviderDetailsForm } from '../../../models/provider/provider-details-form';
import { BusinessRoles } from '../../../models/roles/roles-group';
import { WITHHOLDING_COUNTRY_CODE, WithholdingResidency, WithholdingTaxCode } from '../../../models/withholding/withholding';
import { BusinessProxyService } from '../../../proxies/business.proxy.service';
import { WithholdingTaxProxyService } from '../../../proxies/withholding-tax.proxy.service';
import { EnumService } from '../../../services/enum.service';
import { LocaleHelperService } from '../../../services/locale-helper.service';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';

/** One entry in the left-hand section rail — id doubles as the scroll anchor. */
interface ProviderSection {
  id: string;
  label: string;
  icon: string;
}

/**
 * Provider Details page (#4892).
 *
 * <p>Provider settings used to be a tab on Business Details, saved by that page's Save button along with
 * the name, sites, addresses and representatives. They are their own page now, reached from the Provider
 * role card, and they save on their own through PATCH business/{id}/provider-details — so an officer
 * editing payment terms no longer re-sends (and can no longer clobber) the rest of the business.</p>
 *
 * <p>The page loads the business only to name it in the header and breadcrumb and to confirm the Provider
 * role is actually assigned. Everything the form does not bind — KYC, attachments, the legacy
 * PaymentBank/IntermediateBank pair — is carried through from the loaded record on save, because the PATCH
 * replaces the provider block wholesale and silence would otherwise read as deletion.</p>
 *
 * <p>Tax Authorizations sit at the bottom but are NOT covered by Save: each one is added, edited or revoked
 * against its own endpoint the moment the modal closes. The section says so, since a Save button that
 * quietly does not apply to part of the page is worse than no Save button at all.</p>
 */
@Component({
  selector: 'app-provider-details',
  templateUrl: './provider-details.component.html',
  styleUrl: './provider-details.component.scss',
})
export class ProviderDetailsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly businessProxyService = inject(BusinessProxyService);
  private readonly withholdingProxy = inject(WithholdingTaxProxyService);
  private readonly enumService = inject(EnumService);
  private readonly translateService = inject(TranslateService);
  private readonly localeHelper = inject(LocaleHelperService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly messageService = inject(MessageService);
  private readonly userAuthenticationService = inject(UserAuthenticationService);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly breadcrumbService = inject(BreadcrumbService);

  isLoading = signal(0);
  /** Standard details-page flag. Always false here: a provider block only exists on a saved business. */
  isNew = signal(false);
  currentId = signal<string | null>(null);
  headerTitle = signal<string>(this.t('Provider_Details_Title', 'Provider Details'));

  business = signal<Business | null>(null);

  /**
   * Where this company is (#5121) — read off the business, never edited here. It scopes which withholding
   * codes apply, so the page shows it and says where to change it rather than offering a second copy that
   * could disagree with the business form.
   */
  readonly countryCode = computed(() => this.business()?.countryCode ?? null);
  /** Fixed, non-editable: the jurisdiction we withhold under is Honduras for every provider, whatever the
   *  business's domicile — the domicile above drives the CAI gate, not the withholding-code scope. */
  readonly withholdingCountry = WITHHOLDING_COUNTRY_CODE;
  isReadOnly = signal(true);

  /** The provider block as last read from the API — the base every save is layered onto. */
  private loadedProvider: ProviderDetails | null = null;

  readonly businessLabel = computed(() => {
    const business = this.business();
    if (!business) return '';
    return business.code ? `${business.code} | ${business.name}` : business.name;
  });

  readonly headerSubtitle = computed(() => {
    const label = this.businessLabel();
    const note = this.t('Provider_Details_SavedSeparately', 'saved separately from the business record.');
    return label ? `${label} — ${note}` : note;
  });

  /** Provider role is assigned on the business form; without it there is nothing for this page to save. */
  readonly isProvider = computed(() => (this.business()?.businessTypes ?? []).includes(BusinessEntityType.Provider));
  readonly canEdit = computed(() => !this.isReadOnly() && this.isProvider());

  readonly sections: ProviderSection[] = [
    { id: 'classification', label: this.t('Provider_Section_Classification', 'Classification'), icon: 'pi pi-tags' },
    { id: 'purchasing', label: this.t('Provider_Section_Purchasing', 'Purchasing'), icon: 'pi pi-shopping-cart' },
    { id: 'payment', label: this.t('Provider_Section_Payment', 'Payment & Accounts Payable'), icon: 'pi pi-credit-card' },
    { id: 'banks', label: this.t('Provider_Section_Banking', 'Payable Bank Accounts'), icon: 'pi pi-building-columns' },
    { id: 'tax', label: this.t('Provider_Section_Tax', 'Tax & Fiscal'), icon: 'pi pi-percentage' },
    { id: 'tax-auth', label: this.t('TaxAuthorization_Title', 'Tax Authorizations'), icon: 'pi pi-verified' },
  ];

  /**
   * Sections held back from the page for now.
   *
   * <p>Hidden, not removed: the form controls, the markup and the save payload all stay intact, so a
   * provider's classification and purchasing values still load and are written back untouched by every save
   * made here. Bringing one back is deleting its id from this set.</p>
   */
  private static readonly HIDDEN_SECTIONS: ReadonlySet<string> = new Set(['classification', 'purchasing']);

  isSectionVisible(sectionId: string): boolean {
    return !ProviderDetailsComponent.HIDDEN_SECTIONS.has(sectionId);
  }

  /**
   * Individual fields held back for now, keyed by form control name. Same contract as
   * {@link HIDDEN_SECTIONS}: the control stays in the form and in the save payload, so the stored value
   * survives every save made here — only the input is withheld.
   */
  private static readonly HIDDEN_FIELDS: ReadonlySet<string> = new Set(['taxRegimeCode', 'sgiCode', 'cisCode']);

  isFieldVisible(controlName: string): boolean {
    return !ProviderDetailsComponent.HIDDEN_FIELDS.has(controlName);
  }

  readonly visibleSections: ProviderSection[] = this.sections.filter((section) => this.isSectionVisible(section.id));

  /** The section rail, as a PrimeNG PanelMenu model — each leaf scrolls its section into view. */
  readonly sectionMenu = signal<MenuItem[]>(
    this.visibleSections.map((section) => ({
      label: section.label,
      icon: section.icon,
      command: () => this.scrollToSection(section.id),
    })),
  );

  /** Transient AP control-account name for the selector. Display only — the API derives what it stores (#4785). */
  readonly apAccountNameControl = new FormControl<string | null>(null);

  currencyOptions = signal<{ label: string; value: number }[]>([]);

  /** Provider domicile country (#4304) — drives the AP withholding residency. */

  /** Provider withholding residency (#4304) — shown read-only on AP documents. */
  readonly residencyOptions: { label: string; value: WithholdingResidency }[] = [
    { label: this.t('Provider_Residency_Resident', 'Resident / domiciled'), value: WithholdingResidency.Resident },
    { label: this.t('Provider_Residency_NonResident', 'Non-resident'), value: WithholdingResidency.NonResident },
    { label: this.t('Provider_Residency_Any', 'Any'), value: WithholdingResidency.Any },
  ];

  /** Direct (Standard) vs Intermediary correspondent bank — per provider bank account. */
  readonly bankTypeOptions: { label: string; value: ProviderBankType }[] = [
    { label: this.t('Provider_BankType_Standard', 'Standard'), value: ProviderBankType.Standard },
    { label: this.t('Provider_BankType_Intermediary', 'Intermediary'), value: ProviderBankType.Intermediary },
  ];

  /**
   * What the bank calls the account (#5079). Two of the enum's four values on purpose: PAYROLL and
   * FIXED_TERM are real accounts, but not ones we pay a supplier into, and offering them here would invite
   * a choice nobody should make. Listed rather than derived from the enum for exactly that reason.
   */
  readonly accountTypeOptions: { label: string; value: BankAccountType }[] = [
    { label: this.t('Provider_AccountType_Checking', 'Checking'), value: BankAccountType.CHECKING },
    { label: this.t('Provider_AccountType_Savings', 'Savings'), value: BankAccountType.SAVINGS },
  ];

  withholdingCodes = signal<WithholdingTaxCode[]>([]);
  readonly withholdingCodeOptions = computed(() =>
    this.withholdingCodes().map((code) => ({
      label: `${code.code} — ${this.localeHelper.getLocalizedValue(code.name)} (${code.rate}%)`,
      value: code.code,
    })),
  );

  readonly providerForm = new FormGroup<ProviderDetailsForm>({
    providerTypeCode: new FormControl<string | null>(null),
    commercialName: new FormControl<string | null>(null),
    accountGroupCode: new FormControl<string | null>(null),
    providerBlockReasonCode: new FormControl<string | null>(null),
    isOneTimeVendor: new FormControl<boolean>(false, { nonNullable: true }),
    purchaseOrganizationCode: new FormControl<string | null>(null),
    currencyCode: new FormControl<string | null>(null),
    leadAssociateUserName: new FormControl<string | null>(null),
    lastPurchaseOrderDate: new FormControl<string | null>({ value: null, disabled: true }),
    paymentTermsCode: new FormControl<string | null>(null),
    paymentMethodCode: new FormControl<string | null>(null),
    // Derived API-side from the payment terms — shown, never edited.
    creditDays: new FormControl<number | null>({ value: null, disabled: true }),
    creditLimit: new FormControl<number | null>(null),
    apGlAccountReference: new FormControl<string | null>(null),
    apGlAccountNumber: new FormControl<string | null>(null),
    banks: new FormArray<FormGroup<ProviderBankForm>>([]),
    taxRegimeCode: new FormControl<string | null>(null),
    sgiCode: new FormControl<string | null>(null),
    cisCode: new FormControl<string | null>(null),
    withholdingResidency: new FormControl<WithholdingResidency | null>(null),
    defaultWithholdingCode: new FormControl<string | null>(null),
  });

  constructor() {
    const currencyLabels = this.enumService.getEnum('Currency').values as Record<number, string>;
    this.currencyOptions.set(Object.entries(currencyLabels).map(([key, label]) => ({ label, value: Number(key) })));

    // Residency scopes which withholding codes apply; a default left over from the previous residency would
    // silently pre-select a code the provider cannot legally be withheld under. The jurisdiction is fixed
    // (always HND — we only withhold Honduran taxes), so residency is the only scope that can change here.
    this.providerForm.controls.withholdingResidency.valueChanges.subscribe(() => {
      this.providerForm.controls.defaultWithholdingCode.setValue(null, { emitEvent: false });
      this.loadWithholdingCodes();
    });
  }

  ngOnInit(): void {
    this.isReadOnly.set(!this.userAuthenticationService.roles.includes(BusinessRoles.BusinessAdmin));

    // Path parameter, not `?id=` — this page hangs off the business (#4892: business/{id}/provider-details),
    // so the id belongs in the path. Falls back to the query parameter for links minted before the move.
    const id = this.route.snapshot.paramMap.get('id') ?? this.route.snapshot.queryParamMap.get('id');
    if (!id || id === 'new') {
      GlobalMessageUtil.warn(
        this.t('Provider_Details_Title', 'Provider Details'),
        this.t('Provider_Details_SaveBusinessFirst', 'Save the business first, then set up its provider details.'),
      );
      this.router.navigate(['/businesses']);
      return;
    }

    this.currentId.set(id);
    this.setBreadcrumb(id, null);
    this.loadBusiness(id);
  }

  private t(key: string, fallback: string): string {
    const value = this.translateService.instant(key);
    return value === key ? fallback : value;
  }

  private startLoading(): void {
    this.isLoading.update((count) => count + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((count) => Math.max(0, count - 1));
  }

  private setBreadcrumb(businessId: string, businessLabel: string | null): void {
    this.breadcrumbService.set([
      { label: this.t('Businesses', 'Businesses'), route: '/businesses' },
      { label: businessLabel ?? this.t('Business_Details', 'Business Details'), route: `/business/${businessId}` },
      { label: this.t('Provider_Details_Title', 'Provider Details') },
    ]);
  }

  // ── Load ────────────────────────────────────────────────────────────────────
  private loadBusiness(id: string): void {
    this.startLoading();
    this.businessProxyService
      .getBusinessById(id)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: ({ data }) => {
          if (!data) return;
          this.business.set(data);
          this.setBreadcrumb(id, data.code ? `${data.code} | ${data.name}` : data.name);
          this.populateForm(data.providerDetails ?? null);
        },
      });
  }

  private populateForm(provider: ProviderDetails | null): void {
    this.loadedProvider = provider;

    this.providerForm.patchValue(
      {
        providerTypeCode: provider?.providerTypeCode ?? null,
        commercialName: provider?.commercialName ?? null,
        accountGroupCode: provider?.accountGroupCode ?? null,
        providerBlockReasonCode: provider?.providerBlockReasonCode ?? null,
        isOneTimeVendor: provider?.isOneTimeVendor ?? false,
        purchaseOrganizationCode: provider?.purchaseOrganizationCode ?? null,
        currencyCode: provider?.currencyCode ?? null,
        leadAssociateUserName: provider?.leadAssociateUserName ?? null,
        lastPurchaseOrderDate: provider?.lastPurchaseOrderDate ? String(provider.lastPurchaseOrderDate) : null,
        paymentTermsCode: provider?.paymentTermsCode ?? null,
        paymentMethodCode: provider?.paymentMethodCode ?? null,
        creditDays: provider?.creditDays ?? null,
        creditLimit: provider?.creditLimit ?? null,
        apGlAccountReference: provider?.apGlAccountReference ?? null,
        apGlAccountNumber: provider?.apGlAccountNumber ?? null,
        taxRegimeCode: provider?.taxRegimeCode ?? null,
        sgiCode: provider?.sgiCode ?? null,
        cisCode: provider?.cisCode ?? null,
      },
      { emitEvent: false },
    );

    // Set the withholding trio without emitting: the country subscription exists to clear a default that no
    // longer matches, and on load the saved default is exactly the one to keep.

    this.providerForm.controls.withholdingResidency.setValue(provider?.withholdingResidency ?? null, { emitEvent: false });
    this.providerForm.controls.defaultWithholdingCode.setValue(provider?.defaultWithholdingCode ?? null, { emitEvent: false });
    this.loadWithholdingCodes();

    this.apAccountNameControl.setValue(provider?.apGlAccountName ? this.localeHelper.getLocalizedValue(provider.apGlAccountName) : null);

    this.banks.clear({ emitEvent: false });
    for (const bank of provider?.banks ?? []) {
      this.banks.push(this.newBankGroup(bank), { emitEvent: false });
    }

    if (!this.canEdit()) {
      this.providerForm.disable({ emitEvent: false });
    }
  }

  /** Loads the withholding codes for the fixed jurisdiction (HND) to populate the default-code picker. */
  private loadWithholdingCodes(): void {
    // Scope to the provider's residency — a Resident-only code must not show for a non-resident provider.
    const residency = this.providerForm.controls.withholdingResidency.value;
    this.withholdingProxy.getUsableCodes(this.withholdingCountry, null, residency).subscribe({
      next: (response) => this.withholdingCodes.set(response.success && response.data ? response.data : []),
      error: () => this.withholdingCodes.set([]),
    });
  }

  // ── Banks ───────────────────────────────────────────────────────────────────
  get banks(): FormArray<FormGroup<ProviderBankForm>> {
    return this.providerForm.controls.banks;
  }

  private newBankGroup(bank?: ProviderBankAccount): FormGroup<ProviderBankForm> {
    return new FormGroup<ProviderBankForm>({
      // Stable id so the AP payment request can select and snapshot this bank. Null on a new row — the API
      // mints it (#5143). This used to be `crypto.randomUUID()`, which is why the accounts already on file
      // carry UUIDs; ids are the API's to assign, and a client-invented one is now rejected on save.
      id: new FormControl<string | null>(bank?.id ?? null),
      alias: new FormControl<string | null>(bank?.alias ?? null),
      // Required to match the API (#5143). A row with no bank or no number is not a payment instruction,
      // and the snapshot a payment freezes cannot be built without both.
      bankName: new FormControl<string | null>(bank?.bankName ?? null, [Validators.required]),
      accountNumber: new FormControl<string | null>(bank?.accountNumber ?? null, [Validators.required]),
      swiftBicCode: new FormControl<string | null>(bank?.swiftBicCode ?? null),
      ibanCode: new FormControl<string | null>(bank?.ibanCode ?? null),
      bankAddress: new FormControl<string | null>(bank?.bankAddress ?? null),
      // Required to match the API (#5143): ProviderBankAccount.Currency is no longer optional, and a
      // payment's destination is checked against it. Leaving this optional would let the form save
      // something the API now rejects — and worse, a bank with no currency reads as USD server-side.
      currency: new FormControl<number | null>(bank?.currency ?? null, [Validators.required]),
      bankType: new FormControl<ProviderBankType>(bank?.bankType ?? ProviderBankType.Standard, { nonNullable: true }),
      accountType: new FormControl<BankAccountType>(bank?.accountType ?? BankAccountType.CHECKING, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      isPrimary: new FormControl<boolean>(bank?.isPrimary ?? false, { nonNullable: true }),
    });
  }

  addBank(): void {
    this.banks.push(this.newBankGroup());
  }

  removeBank(index: number): void {
    this.banks.removeAt(index);
  }

  // ── Typed accessors for the AP control-account selector ─────────────────────
  get apReferenceControl(): FormControl<string | null> {
    return this.providerForm.controls.apGlAccountReference;
  }

  get apNumberControl(): FormControl<string | null> {
    return this.providerForm.controls.apGlAccountNumber;
  }

  // ── Save ────────────────────────────────────────────────────────────────────
  save(): void {
    const id = this.currentId();
    if (!id || !this.canEdit()) return;

    this.startLoading();
    this.businessProxyService
      .updateProviderDetails(id, this.buildPayload())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (result) => {
          if (!result.success) return;
          GlobalMessageUtil.success(
            this.t('Provider_Details_Title', 'Provider Details'),
            this.t('Provider_Details_Saved', 'Provider details saved.'),
          );
          // Re-seed from the response so the API-derived credit days and AP account name show immediately.
          this.populateForm(result.data ?? null);
        },
      });
  }

  /**
   * The provider block to persist. Layered over the record as loaded, because the API replaces the block
   * wholesale — anything this form does not bind (KYC, attachments, the legacy bank pair) would otherwise be
   * dropped by a save that never intended to touch it.
   */
  private buildPayload(): ProviderDetails {
    const form = this.providerForm.getRawValue();

    const payload: ProviderDetails = {
      ...(this.loadedProvider ?? {}),
      providerTypeCode: form.providerTypeCode ?? undefined,
      commercialName: form.commercialName ?? undefined,
      accountGroupCode: form.accountGroupCode ?? undefined,
      providerBlockReasonCode: form.providerBlockReasonCode ?? undefined,
      isOneTimeVendor: form.isOneTimeVendor,
      purchaseOrganizationCode: form.purchaseOrganizationCode ?? undefined,
      currencyCode: form.currencyCode ?? undefined,
      leadAssociateUserName: form.leadAssociateUserName ?? undefined,
      paymentTermsCode: form.paymentTermsCode ?? undefined,
      paymentMethodCode: form.paymentMethodCode ?? undefined,
      creditLimit: form.creditLimit ?? undefined,
      apGlAccountReference: form.apGlAccountReference ?? undefined,
      apGlAccountNumber: form.apGlAccountNumber ?? undefined,
      taxRegimeCode: form.taxRegimeCode ?? undefined,
      sgiCode: form.sgiCode ?? undefined,
      cisCode: form.cisCode ?? undefined,
      withholdingResidency: form.withholdingResidency ?? undefined,
      defaultWithholdingCode: form.defaultWithholdingCode ?? undefined,
      banks: form.banks.map((bank) => ({
        id: bank.id,
        alias: bank.alias,
        bankName: bank.bankName,
        accountNumber: bank.accountNumber,
        swiftBicCode: bank.swiftBicCode,
        ibanCode: bank.ibanCode,
        bankAddress: bank.bankAddress,
        currency: bank.currency,
        bankType: bank.bankType,
        accountType: bank.accountType,
        isPrimary: bank.isPrimary,
      })),
    };

    // Credit days and the AP account name are the API's to decide (#4785) — sending ours back would only
    // give it a stale value to reconcile.
    delete payload.creditDays;
    delete payload.apGlAccountName;
    return payload;
  }

  // ── Navigation ──────────────────────────────────────────────────────────────
  /** Back to the business this provider block belongs to. */
  back(): void {
    const id = this.currentId();
    this.router.navigate(id ? ['/business', id] : ['/businesses']);
  }

  scrollToSection(sectionId: string): void {
    const element = (this.host.nativeElement as HTMLElement).querySelector(`#${sectionId}`);
    element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // ── Master-data dropdown plumbing (shared with the rest of Finance.UI) ───────
  deleteItemMasterData(item: { item: MasterData; confirmCallback: () => void; cancelCallback: () => void }): void {
    this.confirmationService.confirm({
      message: this.t('MasterData_Delete_Confirm', 'Are you sure you want to delete item?'),
      header: this.t('Confirmation', 'Confirmation'),
      icon: 'pi pi-exclamation-triangle',
      key: 'ct',
      accept: () => item.confirmCallback(),
      reject: () => item.cancelCallback(),
    });
  }

  showMessagesApi(event: { Success?: boolean }, message?: string): void {
    if (event.Success) {
      this.messageService.add({ severity: 'success', summary: 'Success', detail: message, life: 2500, key: 'main' });
    }
  }
}
